import React, { useState, useCallback, useEffect, useRef, useMemo } from "react";
import { useTranslation } from "react-i18next";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { getTime, scrollToBottom } from "./ChatHandler";
import { Message } from "./ChatInterface";
import { CloseOutlined } from "@ant-design/icons";
import Tippy from "@tippyjs/react";
import "tippy.js/dist/tippy.css";
import { fetchCampaignListsAndCompany } from "src/services/EngagementAPIs";
import { useNavigate } from "react-router-dom";
import { Avatar, message } from "antd";
import ChatInput from "./ChatInput";
import {
  ChartLine,
  CircleDollarSign,
  Expand,
  ImagePlus,
  ListPlus,
  Search,
  Volume2,
} from "lucide-react";
import { createMessageHandler } from "src/handlers/MessageHandlers";
import ChatCampaignWizard from "./CampaignWizard/ChatCampaignWizard";
import ChatTemplateCampaignWizard from "./CampaignWizard/ChatTemplateCampaignWizard";
import ChatWhatsAppCampaignWizard from "./CampaignWizard/ChatWhatsAppCampaignWizard";
import ChatCampaignSetupStep from "./CampaignWizard/steps/ChatCampaignSetupStep";
import { useCampaignStatusSync } from "src/hooks/UseCampaignStatusSync";
import { useChatCampaignFlow } from "./CampaignWizard/UseChatCampaignFlow";
import { resetChatSession } from "src/services/ChatbotAPI";
import { useLanguage } from "src/hooks/UseLanguage";
import { getApiLangFromI18n } from "src/utils/apiLang";
import {
  audienceListMatchesSearch,
  CREATE_NEW_LIST_NAME,
  registerAudienceListLabels,
  toAudienceListOptions,
  type AudienceListOption,
} from "src/utils/audienceListLabels";

// TEMP: when true, launching the campaign wizard from the widget popup
// redirects to the full-screen chat instead of rendering inline. Flip back
// to false to restore the inline popup wizard.
const REDIRECT_CAMPAIGN_WIZARD_TO_FULL_SCREEN = true;

const Chatbot = () => {
  const { t, i18n } = useTranslation();
  const { isRTL } = useLanguage();
  const chatbotRef = useRef<HTMLDivElement>(null);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const [isOpen, setIsOpen] = useState(false);
  const [query, setQuery] = useState<Message[]>([]);
  useCampaignStatusSync(query);
  const campaignFlow = useChatCampaignFlow({
    onAdvance: snapshot => {
      setQuery(prev => {
        const updated = [...prev];
        for (let i = updated.length - 1; i >= 0; i -= 1) {
          if (updated[i].type === "campaignWizard") {
            updated[i] = { ...updated[i], campaignFlowSnapshot: snapshot };
            break;
          }
        }
        return [...updated, { sender: "bot", timestamp: getTime(), type: "campaignWizard" }];
      });
    },
  });
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [firstMessageSent, setFirstMessageSent] = useState(false);
  const [suggestedQuestions, setSuggestedQuestions] = useState<string[]>([]);
  const [menuOpen, setMenuOpen] = useState(false);
  const [isFetching, setIsFetching] = useState(false);
  const [isScheduledOrSent] = useState(false);
  const isScheduledOrSentRef = useRef(isScheduledOrSent);
  const [conversationStep, setConversationStep] = useState<
    | "impressionsStep"
    | "roasStep"
    | "conversionsStep"
    | "engagementStep"
    | "askAudience"
    | "askWebsite"
    | "confirmCampaign"
    | null
  >(null);
  const clearedRef = useRef(false);
  const [campaignName] = useState("");
  const [audience] = useState("");
  const user_id = localStorage.getItem("user_id");
  const tenant_id = localStorage.getItem("tenant_id") || "";
  const [position, setPosition] = useState(() => ({
    x: isRTL ? 20 : window.innerWidth - 100,
    y: window.innerHeight - 100,
  }));
  const [selectedFile, setSelectedFile] = useState<File | File[] | null>(null);
  const [previewURL, setPreviewURL] = useState<string | null>(null);
  const dragging = useRef(false);
  const dragged = useRef(false);
  const offset = useRef({ x: 0, y: 0 });
  const viewportSizeRef = useRef({
    width: window.innerWidth,
    height: window.innerHeight,
  });
  const [audiences, setAudiences] = useState<AudienceListOption[]>([]);
  const [audienceListSearch, setAudienceListSearch] = useState("");
  const [, setInputDisabled] = useState(false);
  const [queryLimitApiMessage, setQueryLimitApiMessage] = useState<string | undefined>(undefined);
  const navigate = useNavigate();

  const goToFullScreenChat = useCallback(() => {
    const liveChatState = {
      query,
      firstMessageSent,
      suggestedQuestions,
      conversationStep,
      campaignName,
      audience,
    };

    try {
      sessionStorage.setItem("ai_chat_live_state", JSON.stringify(liveChatState));
    } catch (_error) {
      // Ignore storage failures and continue with route state.
    }

    navigate("/account/ai-chat", {
      state: {
        preserveSession: false,
        liveChatState,
      },
    });
  }, [audience, campaignName, conversationStep, firstMessageSent, navigate, query, suggestedQuestions]);

  useEffect(() => {
    if (!REDIRECT_CAMPAIGN_WIZARD_TO_FULL_SCREEN) {return;}
    if (!query.some(msg => msg.type === "campaignWizard")) {return;}
    goToFullScreenChat();
  }, [query, goToFullScreenChat]);

  const quickActions: {
    value: string;
    title: string;
    subtitle: string;
    icon: React.ReactNode;
  }[] = [
    {
      value: "Create New Campaign",
      title: t("chatScreen.quickActionCreateCampaignTitle"),
      subtitle: t("chatScreen.quickActionCreateCampaignSubtitle"),
      icon: <Volume2 size={20} />,
    },
    {
      value: "Impressions",
      title: t("chatScreen.quickActionAnalyseScreenshotTitle"),
      subtitle: t("chatScreen.quickActionAnalyseScreenshotSubtitle"),
      icon: <ImagePlus size={20} />,
    },
    {
      value: "Engagement & Likes",
      title: t("chatScreen.quickActionEngagementImpressionsTitle"),
      subtitle: t("chatScreen.quickActionEngagementImpressionsSubtitle"),
      icon: <ChartLine size={20} />,
    },
    {
      value: "Return on Ad Spend",
      title: t("chatScreen.quickActionRoasTitle"),
      subtitle: t("chatScreen.quickActionRoasSubtitle"),
      icon: <CircleDollarSign size={20} />,
    },
    {
      value: "Create New List",
      title: t("chatScreen.quickActionCreateListTitle"),
      subtitle: t("chatScreen.quickActionCreateListSubtitle"),
      icon: <ListPlus size={20} />,
    },
  ];

  const filteredExistingAudiences = useMemo(
    () =>
      audiences
        .filter(a => a.name !== CREATE_NEW_LIST_NAME)
        .filter(a => audienceListMatchesSearch(a, audienceListSearch)),
    [audiences, audienceListSearch],
  );

  useEffect(() => {
    if (conversationStep !== "askAudience") {
      setAudienceListSearch("");
    }
  }, [conversationStep]);

  useEffect(() => {
    resetChatSession();
  }, []);

  useEffect(() => {
    scrollToBottom(messagesEndRef);
  }, [query, loading]);

  useEffect(() => {
    isScheduledOrSentRef.current = isScheduledOrSent;
  }, [isScheduledOrSent]);

  const apiLang = getApiLangFromI18n(i18n);

  useEffect(() => {
    const createNewListOption: AudienceListOption = {
      name: CREATE_NEW_LIST_NAME,
      displayName: t("chatScreen.createNewList"),
    };

    const loadAudiences = async () => {
      try {
        const apiKey = localStorage.getItem("api_key") || "";
        const tenantId = localStorage.getItem("tenant_id") || "";

        if (!apiKey || !tenantId) {
          message.error(t("chatScreen.missingApiCredentials"));
          return;
        }

        const response = await fetchCampaignListsAndCompany(tenantId, apiKey, apiLang);
        setAudiences(
          registerAudienceListLabels([
            ...toAudienceListOptions(response),
            createNewListOption,
          ]),
        );
      } catch (_error) {
        const listOption = (key: string) =>
          t(`audienceOverview.createSegment.listOptions.${key}`);
        setAudiences(
          registerAudienceListLabels([
            { name: "Loyal Customers", displayName: listOption("loyalCustomers") },
            { name: "High Spenders", displayName: listOption("highSpenders") },
            { name: "Cart Abandoners", displayName: listOption("cartAbandoners") },
            createNewListOption,
          ]),
        );
      }
    };

    loadAudiences();
  }, [t, apiLang]);

  useEffect(() => {
    if (isOpen) {
      const listener = (event: MouseEvent) => {
        if (
          chatbotRef.current &&
          !chatbotRef.current.contains(event.target as Node) &&
          !(event.target as HTMLElement).closest(".sidebar")
        ) {
          setIsOpen(false);
          setMenuOpen(false);
        }
      };
      document.addEventListener("mousedown", listener);
      return () => document.removeEventListener("mousedown", listener);
    }
  }, [isOpen]);

  const startNewChat = () => {
    clearedRef.current = true;
    resetChatSession();
    setQuery([]);
    setFirstMessageSent(false);
    setConversationStep(null);
    setSuggestedQuestions([]);
    setIsFetching(false);
    setLoading(false);
    setMenuOpen(false);
    setQueryLimitApiMessage(undefined);
    campaignFlow.resetFlow();
  };

  const handleClearChat = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.stopPropagation();
    startNewChat();
  };

  const handleQueryLimitReached = useCallback((backendDetail: string) => {
    const messageFromApi = backendDetail.trim();
    setQueryLimitApiMessage(messageFromApi);
    setQuery(prev => {
      if (prev.some(msg => msg.type === "limitNotice")) {
        return prev;
      }
      if (!messageFromApi) {
        return prev;
      }
      return [
        ...prev,
        {
          sender: "bot",
          type: "limitNotice",
          text: messageFromApi,
          timestamp: getTime(),
        },
      ];
    });
    setLoading(false);
    setIsFetching(false);
    setSuggestedQuestions([]);
  }, []);

  const messageHandler = createMessageHandler({
    textToSend: input,
    conversationStep,
    input,
    selectedFile,
    previewURL,
    campaignName,
    user_id,
    tenant_id,
    clearedRef,
    setQuery,
    setInput,
    setConversationStep,
    setLoading,
    setIsFetching,
    setFirstMessageSent,
    setSuggestedQuestions,
    onQueryLimitReached: handleQueryLimitReached,
  });

  const handleSendMessage = async (messageText?: string) => {
    if (queryLimitApiMessage !== undefined) {
      return;
    }
    const textToSend = messageText ?? input;
    const hasText = textToSend.trim().length > 0;
    const hasFile = !!selectedFile;
    if ((!hasText && !hasFile) || isFetching) {return;}

    clearedRef.current = false;

    if (!firstMessageSent) {setFirstMessageSent(true);}

    try {
      await messageHandler(hasText ? textToSend : "");
    } finally {
      // Always clear input and file regardless of success/failure
      setInput("");
      setSelectedFile(null);
      setPreviewURL(null);
    }
  };


  const getLauncherSize = useCallback(() => {
    const el = document.querySelector(".chatbot-border") as HTMLElement | null;
    return {
      width: el?.offsetWidth || 80,
      height: el?.offsetHeight || 80,
    };
  }, []);

  const getViewportSize = useCallback(() => {
    const viewport = window.visualViewport;
    return {
      width: viewport?.width ?? window.innerWidth,
      height: viewport?.height ?? window.innerHeight,
    };
  }, []);

  const clampLauncherPosition = useCallback(
    (x: number, y: number, viewport = getViewportSize()) => {
      const { width: elWidth, height: elHeight } = getLauncherSize();
      return {
        x: Math.max(0, Math.min(Math.max(0, viewport.width - elWidth), x)),
        y: Math.max(0, Math.min(Math.max(0, viewport.height - elHeight), y)),
      };
    },
    [getLauncherSize, getViewportSize],
  );

  useEffect(() => {
    // Preserve distance from the right/bottom edges across zoom/resize so the
    // launcher stays in its intended corner instead of drifting toward center.
    const keepLauncherAnchored = () => {
      const nextViewport = getViewportSize();
      const prevViewport = viewportSizeRef.current;
      const { width: elWidth, height: elHeight } = getLauncherSize();

      setPosition(prev => {
        const fromRight = prevViewport.width - prev.x - elWidth;
        const fromBottom = prevViewport.height - prev.y - elHeight;
        return clampLauncherPosition(
          nextViewport.width - elWidth - fromRight,
          nextViewport.height - elHeight - fromBottom,
          nextViewport,
        );
      });

      viewportSizeRef.current = nextViewport;
    };

    viewportSizeRef.current = getViewportSize();
    window.addEventListener("resize", keepLauncherAnchored);
    window.visualViewport?.addEventListener("resize", keepLauncherAnchored);

    return () => {
      window.removeEventListener("resize", keepLauncherAnchored);
      window.visualViewport?.removeEventListener("resize", keepLauncherAnchored);
    };
  }, [clampLauncherPosition, getLauncherSize, getViewportSize]);

  const handleMouseDown = (e: React.MouseEvent<HTMLDivElement>) => {
    e.preventDefault();

    dragged.current = false;
    dragging.current = true;

    offset.current = {
      x: e.clientX - position.x,
      y: e.clientY - position.y,
    };

    window.addEventListener("mousemove", handleMouseMove);
    window.addEventListener("mouseup", handleMouseUp);
  };

  const handleMouseMove = (e: MouseEvent) => {
    if (!dragging.current || e.buttons === 0) {return;}

    dragged.current = true;
    setPosition(
      clampLauncherPosition(
        e.clientX - offset.current.x,
        e.clientY - offset.current.y,
      ),
    );
  };

  const handleMouseUp = () => {
    dragging.current = false;
    window.removeEventListener("mousemove", handleMouseMove);
    window.removeEventListener("mouseup", handleMouseUp);
  };

  const handleClick = () => {
    if (dragged.current) {return;}
    setIsOpen(true);
  };

  const handleCampaignConfirmation = (option: string) => {
    const userMessage: Message = {
      sender: "user",
      text: option === "Yes" ? t("chatScreen.yes") : option === "No" ? t("chatScreen.no") : option,
      timestamp: getTime(),
    };
    setQuery(prev => [...prev, userMessage]);

    if (option === "Yes") {
      const wizardMessage: Message = {
        sender: "bot",
        timestamp: getTime(),
        type: "campaignWizard",
      };

      setQuery(prev => [...prev, wizardMessage]);
      setConversationStep(null);
      setFirstMessageSent(true);
      setSuggestedQuestions([]);
      setInput("");
    } else {
      const botMessage: Message = {
        sender: "bot",
        text: t("chatScreen.okayNoProblem"),
        timestamp: getTime(),
      };
      setQuery(prev => [...prev, botMessage]);
      setConversationStep(null);
      setSuggestedQuestions([
        "Create New Campaign",
        "Show campaign performance",
        "View reports",
      ]);
      setInputDisabled(false);
    }
  };

  const handleSuggestedQuestionClick = (question: string) => {
    if (question === "Return on Ad Spend") {
      const userMessage: Message = {
        sender: "user",
        text: t("chatScreen.returnOnAdSpend"),
        timestamp: getTime(),
      };
      setQuery(prev => [...prev, userMessage]);

      const botMessage: Message & { type?: string } = {
        sender: "bot",
        text: t("chatScreen.roasReply"),
        timestamp: getTime(),
        type: "botQuestion",
      };
      setQuery(prev => [...prev, botMessage]);

      setConversationStep("roasStep");
      setFirstMessageSent(true);
      setSuggestedQuestions([]);
      setInput("");
      return;
    }

    if (question === "Conversions") {
      const userMessage: Message = {
        sender: "user",
        text: t("chatScreen.conversions"),
        timestamp: getTime(),
      };
      setQuery(prev => [...prev, userMessage]);

      const botMessage: Message & { type?: string } = {
        sender: "bot",
        text: t("chatScreen.conversionsReply"),
        timestamp: getTime(),
        type: "botQuestion",
      };
      setQuery(prev => [...prev, botMessage]);

      setConversationStep("conversionsStep");
      setFirstMessageSent(true);
      setSuggestedQuestions([]);
      setInput("");
      return;
    }

    if (question === "Impressions") {
      const userMessage: Message = {
        sender: "user",
        text: t("chatScreen.impressions"),
        timestamp: getTime(),
      };
      setQuery(prev => [...prev, userMessage]);

      const botMessage: Message & { type?: string } = {
        sender: "bot",
        text: t("chatScreen.impressionsReply"),
        timestamp: getTime(),
        type: "botQuestion",
      };
      setQuery(prev => [...prev, botMessage]);

      setConversationStep("impressionsStep");
      setFirstMessageSent(true);
      setSuggestedQuestions([]);
      setInput("");
      return;
    }

    if (question === "Engagement & Likes" || question === "Engagements & Likes") {
      const userMessage: Message = {
        sender: "user",
        text: t("chatScreen.engagementAndLikes"),
        timestamp: getTime(),
      };
      setQuery(prev => [...prev, userMessage]);

      const botMessage: Message & { type?: string } = {
        sender: "bot",
        text: t("chatScreen.engagementReply"),
        timestamp: getTime(),
        type: "botQuestion",
      };
      setQuery(prev => [...prev, botMessage]);

      setConversationStep("engagementStep");
      setFirstMessageSent(true);
      setSuggestedQuestions([]);
      setInput("");
      return;
    }

    // "Create New Campaign" and "Create New List" are sent to the Ask DataTram
    // API like any other prompt. The backend classifies the message (via the
    // `intent` field) and the UI reacts to that, instead of a hardcoded script.
    setInput(question);
    setSuggestedQuestions([]);
    handleSendMessage(question);
  };

  const WaveIcon = () => (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width="16"
      height="16"
      viewBox="0 0 35 32"
      fill="none"
    >
      <path
        d="M1 30.5C12.2113 27.974 5.52192 2.0716 17 1.5C28.5918 0.922731 22.2352 27.7056 33.5 30.5"
        stroke="white"
        strokeWidth="3"
        strokeLinecap="round"
      />
    </svg>
  );

  return (
    <div
      ref={chatbotRef}
      className={`chatbot-container ${isOpen ? "open" : "closed"}`}
      onClick={() => menuOpen && setMenuOpen(false)}
    >
      {!isOpen && (
        <div
          className="chatbot-border draggable tooltip-container"
          onMouseDown={handleMouseDown}
          onClick={handleClick}
          style={{ left: position.x, top: position.y, right: "auto" }}
        >
          <img
            src="/Icons/chatbot-logo.png"
            alt="Logo"
            className="askDT-logo"
          />
          <span
            className={`tooltip-text tooltip-text--${position.x < window.innerWidth / 2 ? "left" : "right"}`}
          >
            {t("chatbot.launcherTooltip")}
          </span>
        </div>
      )}
      {isOpen && (
        <div className="chatbox">
          <div className="chatbox-header blue-border">
            <div className="chatbox-header-left">
              <img
                src="/Icons/chatbot-logo.png"
                alt="Logo"
                className="askDT-logo-inside"
              />
              <span className="chatbot-heading">{t("sidebar.sections.askDataTram")}</span>
            </div>

            <div className="chatbox-header-actions">
              <Tippy content={t("chatbot.openFullScreen")} placement="bottom">
                <Expand
                  onClick={e => {
                    e.stopPropagation();
                    goToFullScreenChat();
                  }}
                />
              </Tippy>

              <Tippy content={t("chatbot.moreOptions")} placement="bottom">
                <div
                  className="chatbot-menu-wrapper"
                  onClick={e => {
                    e.stopPropagation();
                    setMenuOpen(open => !open);
                  }}
                >
                  <span className="chatbot-menu-dots">⋯</span>
                  {menuOpen && (
                    <div className="chatbot-menu-dropdown">
                      <button onClick={handleClearChat}>
                        {t("chatScreen.clearChat")}
                      </button>
                    </div>
                  )}
                </div>
              </Tippy>

              <Tippy content={t("chatbot.closeChat")} placement="bottom">
                <CloseOutlined
                  className="openclose-Icons"
                  onClick={() => setIsOpen(false)}
                />
              </Tippy>
            </div>
          </div>
          <div
            className={`conversation-area ${
              !firstMessageSent && !suggestedQuestions.length
                ? "without-suggestions"
                : ""
            }`}
          >
            {!firstMessageSent && conversationStep === null && (
              <>
                <div className="chatbot-welcome-container">
                  <div className="chatbot-avatar">
                    <Avatar
                      size={35}
                      icon={<WaveIcon />}
                      alt="Chatbot Avatar"
                    />
                  </div>
                  <div className="chatbot-welcome-message">
                    {t("chatbot.welcomeMessage")}
                  </div>
                </div>

                <div className="quick-action-buttons">
                  {quickActions.map((action, idx: number) => (
                    <button
                      key={idx}
                      className="chatbot-action-button"
                      onClick={() => handleSuggestedQuestionClick(action.value)}
                    >
                      <span className="chatbot-action-left">
                        <span className="chatbot-action-icon">{action.icon}</span>
                        <span className="chatbot-action-content">
                          <span className="chatbot-action-title">{action.title}</span>
                          <span className="chatbot-action-subtitle">
                            {action.subtitle}
                          </span>
                        </span>
                      </span>
                      <span className="arrow">
                        <img src="/Icons/smallarrow.svg" alt="arrow" />
                      </span>
                    </button>
                  ))}
                </div>
              </>
            )}

            <div className="chat-messages">
              {query.map((msg, index) => (
                <div key={index}>
                  {/* SHOW IMAGE FIRST if it exists */}
                  {msg.file && msg.file?.type?.startsWith("image/") && (
                    <div className={`message-row ${msg.sender}`}>
                      {msg.sender === "bot" && (
                        <div className="chatbot-avatar">
                          <Avatar
                            size={35}
                            icon={<WaveIcon />}
                            alt="Chatbot Avatar"
                          />
                        </div>
                      )}
                      <div
                        className={`message-bubble ${
                          msg.sender === "bot" ? "bot-bubble" : "user-bubble"
                        }`}
                        style={{
                          background: "transparent",
                          padding: "0",
                          boxShadow: "none",
                        }}
                      >
                        <img
                          src={msg.file.url || undefined}
                          alt={msg.file.name}
                          className="chat-image-preview"
                          onError={e => {
                            console.error(
                              "Failed to load image:",
                              msg.file?.url,
                            );
                            e.currentTarget.style.display = "none";
                          }}
                          style={{
                            maxWidth: "100%",
                            maxHeight: "400px",
                            borderRadius: "8px",
                            marginBottom: msg.text ? "12px" : "0",
                            display: "block",
                            objectFit: "contain",
                          }}
                        />
                      </div>
                    </div>
                  )}

                  {/* SHOW TEXT AFTER IMAGE */}
                  {msg.text && msg.type !== "limitNotice" && (
                    <div
                      className={`message-row ${msg.sender} ${
                        msg.type === "botQuestion" ? "bot-question" : ""
                      }`}
                    >
                      {msg.sender === "bot" && !msg.file && (
                        <div className="chatbot-avatar">
                          <Avatar
                            size={35}
                            icon={<WaveIcon />}
                            alt="Chatbot Avatar"
                          />
                        </div>
                      )}

                      <div
                        className={`message-bubble ${
                          msg.sender === "bot" ? "bot-bubble" : "user-bubble"
                        }`}
                      >
                        {msg.sender === "bot" ? (
                          <ReactMarkdown remarkPlugins={[remarkGfm]}>
                            {msg.text}
                          </ReactMarkdown>
                        ) : (
                          msg.text
                        )}
                      </div>
                    </div>
                  )}

                  {/* SHOW NON-IMAGE FILE LINK */}
                  {msg.file && !msg.file?.type?.startsWith("image/") && (
                    <div className={`message-row ${msg.sender}`}>
                      {msg.sender === "bot" && !msg.text && (
                        <div className="chatbot-avatar">
                          <Avatar
                            size={35}
                            icon={<WaveIcon />}
                            alt="Chatbot Avatar"
                          />
                        </div>
                      )}
                      <div
                        className={`message-bubble ${
                          msg.sender === "bot" ? "bot-bubble" : "user-bubble"
                        }`}
                      >
                        <a
                          href={msg.file.url || undefined}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="chat-file-link"
                        >
                          📎 {msg.file.name}
                        </a>
                      </div>
                    </div>
                  )}

                  {msg.type === "campaignWizard" && (() => {
                    const cancelWizard = () => {
                      setConversationStep(null);
                      setSuggestedQuestions([
                        "Create New Campaign",
                        "Show campaign performance",
                        "View reports",
                      ]);
                    };
                    const resolvedPhase = msg.campaignFlowSnapshot?.phase ?? campaignFlow.phase;
                    const frozenEmailSnapshot =
                      msg.campaignFlowSnapshot?.phase === "email"
                        ? msg.campaignFlowSnapshot.snapshot
                        : undefined;
                    const frozenTemplateSnapshot =
                      msg.campaignFlowSnapshot?.phase === "template"
                        ? msg.campaignFlowSnapshot.snapshot
                        : undefined;
                    const frozenWhatsAppSnapshot =
                      msg.campaignFlowSnapshot?.phase === "whatsapp"
                        ? msg.campaignFlowSnapshot.snapshot
                        : undefined;

                    return (
                      <div className="category-bubble-container">
                        <div className="chatbot-avatar">
                          <Avatar size={35} icon={<WaveIcon />} alt="Chatbot Avatar" />
                        </div>
                        <div className="category-selection-container">
                          {resolvedPhase === "setup" && (
                            <div
                              className={`campaign-flow chat-campaign-wizard${
                                msg.campaignFlowSnapshot ? " chat-campaign-wizard--frozen" : ""
                              }`}
                            >
                              <ChatCampaignSetupStep
                                setup={campaignFlow.setup}
                                onCancel={cancelWizard}
                                frozen={Boolean(msg.campaignFlowSnapshot)}
                                onDraftSaved={startNewChat}
                              />
                            </div>
                          )}
                          {resolvedPhase === "email" && (
                            <ChatCampaignWizard
                              wizard={campaignFlow.emailWizard}
                              frozenSnapshot={frozenEmailSnapshot}
                              onBackToSetup={campaignFlow.backToSetup}
                              onDraftSaved={startNewChat}
                            />
                          )}
                          {resolvedPhase === "template" && (
                            <ChatTemplateCampaignWizard
                              wizard={campaignFlow.templateWizard}
                              frozenSnapshot={frozenTemplateSnapshot}
                              onDraftSaved={startNewChat}
                            />
                          )}
                          {resolvedPhase === "whatsapp" && (
                            <ChatWhatsAppCampaignWizard
                              wizard={campaignFlow.whatsAppWizard}
                              frozenSnapshot={frozenWhatsAppSnapshot}
                            />
                          )}
                        </div>
                      </div>
                    );
                  })()}
                  {msg.type === "confirmCampaign" && (
                    <div className="confirm-buttons">
                      {msg.options?.map((option: string, index: number) => (
                        <button
                          key={index}
                          className={`button ${
                            option === "Yes"
                              ? "button button--primary"
                              : "button button--outline"
                          }`}
                          onClick={() => handleCampaignConfirmation(option)}
                        >
                          {option === "Yes"
                            ? t("chatScreen.yes")
                            : option === "No"
                              ? t("chatScreen.no")
                              : option}
                        </button>
                      ))}
                    </div>
                  )}

                  {msg.type === "menuQuestions" && msg.menu && (
                    <div className="message bot">
                      <div className="message-text">
                        {t("chatScreen.relatedQuestions")}
                      </div>
                      <div className="menu-questions-grid">
                        {msg.menu?.questions?.map((q, idx) => (
                          <button
                            key={idx}
                            className="campaign-option-button"
                            onClick={() => handleSendMessage(q.text)}
                          >
                            <img
                              src={q.icon}
                              alt="q-icon"
                              className="menu-question-icon"
                            />
                            <span>{q.text}</span>
                          </button>
                        ))}
                      </div>
                    </div>
                  )}

                  {msg.type === "limitNotice" && msg.text && (
                    <div className="message-row bot system-notice-row">
                      <div className="chatbot-avatar">
                        <Avatar size={35} icon={<WaveIcon />} />
                      </div>
                      <div className="message-bubble system-notice-bubble">
                        {msg.text}
                      </div>
                    </div>
                  )}
                </div>
              ))}
              {/* Show audience options ONLY at askAudience step */}

              {conversationStep === "askAudience" && (
                <div className="audience-selection-container">
                  {Array.isArray(audiences) && audiences.length > 0 && (
                    <>
                      <div className="audience-list-heading">
                        {t("chatScreen.selectYourList")}
                      </div>
                      {audienceListSearch.trim() &&
                        filteredExistingAudiences.length === 0 && (
                          <p className="audience-list-empty-filter" role="status">
                            {t("chatScreen.noListsMatchSearch")}
                          </p>
                        )}
                      <div className="campaign-options-grid audience-list-grid-with-search">
                        <div
                          className="audience-list-search-cell"
                          dir={isRTL ? "rtl" : "ltr"}
                        >
                          <Search
                            size={12}
                            className="audience-list-search-icon"
                            aria-hidden
                          />
                          <input
                            type="search"
                            className="audience-list-search-input"
                            value={audienceListSearch}
                            onChange={e => setAudienceListSearch(e.target.value)}
                            placeholder={t("chatScreen.searchListsPlaceholder")}
                            autoComplete="off"
                            spellCheck={false}
                            aria-label={t("chatbot.searchListsAriaLabel")}
                          />
                        </div>
                        {filteredExistingAudiences.map((audience, idx) => (
                          <button
                            key={`${audience.name}-${idx}`}
                            className="campaign-option-button"
                            /* Send the canonical `name` — segment lookup matches on it, not the label. */
                            onClick={() => handleSendMessage(audience.name)}
                          >
                            {audience.displayName}
                            <span className="arrow">
                              <img src="/Icons/smallarrow.svg" alt="arrow" />
                            </span>
                          </button>
                        ))}
                      </div>
                      {/* TEMP: "Create New List" hidden in the campaign audience step (chatbot flow only). Re-enable by changing `false` back to `true`. */}
                      {false && (
                        <>
                          <div className="custom-list-heading">
                            {t("chatScreen.createCustomList")}
                          </div>
                          <div className="campaign-options-grid">
                            {audiences
                              .filter(aud => aud.name === CREATE_NEW_LIST_NAME)
                              .map((audience, idx) => (
                                <button
                                  key={idx}
                                  className="campaign-option-button"
                                  onClick={() => handleSendMessage(audience.name)}
                                >
                                  {audience.displayName}
                                  <span className="arrow">
                                    <img src="/Icons/smallarrow.svg" alt="arrow" />
                                  </span>
                                </button>
                              ))}
                          </div>
                        </>
                      )}
                    </>
                  )}
                </div>
              )}

              {loading && (
                <div className="message-row bot typing-message">
                  <div className="chatbot-avatar">
                    <Avatar
                      size={35}
                      icon={<WaveIcon />}
                      alt="Chatbot Avatar"
                    />
                  </div>
                  <div className="message-bubble bot-bubble typing-bubble">
                    <span className="typing-text">{t("chatScreen.typing")}</span>
                    <div className="typing-indicator">
                      <span></span>
                      <span></span>
                      <span></span>
                    </div>
                  </div>
                </div>
              )}
              <div ref={messagesEndRef} />
            </div>
          </div>

          {suggestedQuestions.length > 0 && (
            <div className="compact-suggestions-container">
              <div className="compact-suggestions-header">
                {t("chatScreen.suggestedQuestionsHeader")}
              </div>
              <div className="compact-suggestions-scroll">
                {suggestedQuestions.map((question, index) => (
                  <button
                    className="compact-pill-button"
                    key={index}
                    onClick={() => handleSuggestedQuestionClick(question)}
                  >
                    {question === "Create New Campaign"
                      ? t("chatScreen.createNewCampaign")
                      : question === "Create New List"
                        ? t("chatScreen.quickActionCreateListTitle")
                        : question === "Show campaign performance"
                          ? t("chatScreen.showCampaignPerformance")
                          : question === "View reports"
                            ? t("chatScreen.viewReports")
                            : question === "Return on Ad Spend"
                              ? t("chatScreen.returnOnAdSpend")
                              : question === "Engagement & Likes" ||
                                  question === "Engagements & Likes"
                                ? t("chatScreen.engagementAndLikes")
                                : question === "Conversions"
                                  ? t("chatScreen.conversions")
                                  : question === "Impressions"
                                    ? t("chatScreen.impressions")
                                    : question}
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* ⌨️ Input */}
          <ChatInput
            input={input}
            setInput={setInput}
            handleSendMessage={handleSendMessage}
            isFetching={isFetching}
            isInputLocked={queryLimitApiMessage !== undefined}
            inputHelperText={queryLimitApiMessage || undefined}
            selectedFile={selectedFile}
            setSelectedFile={setSelectedFile}
            previewURL={previewURL}
            setPreviewURL={setPreviewURL}
          />
        </div>
      )}
    </div>
  );
};

export default Chatbot;
